import { Cache, ResolverConfig } from "@urql/exchange-graphcache";

import { relayPagination } from "./relayPaginationCustom";
import {
  QueryGetBlueprintByIdArgs,
  QueryGetBlueprintRunByIdArgs,
  QueryGetBlueprintVersionByIdArgs,
  QueryGetFileByIdArgs,
  QueryGetSessionByIdArgs,
  QueryGetStyleByIdArgs,
  QueryGetWorkspaceByIdArgs,
  QueryGetWorkspaceByNameArgs,
  WorkspaceAssetArgs,
  WorkspaceStyleArgs,
} from "./schema.graphql";

const relayResolver = relayPagination({ inputPath: "input" });

/**
 * Check whether an entity already has data in the normalized cache.
 * Returns false for entities that have never been fetched, preventing
 * resolvers from returning a reference that graphcache would resolve
 * to `null` (suppressing the network request for nullable fields).
 */
const isCached = (cache: Cache, typename: string, id: unknown): boolean => {
  if (id == null) return false;
  const key = cache.keyOfEntity({ __typename: typename, id: id as string });
  return key != null && cache.resolve(key, "__typename") != null;
};

export const resolverConfig: Partial<ResolverConfig> = {
  Query: {
    getWorkspaceById: (_result, args: QueryGetWorkspaceByIdArgs, cache) => {
      if (!isCached(cache, "Workspace", args.id)) return undefined;
      return { __typename: "Workspace", id: args.id };
    },
    getWorkspaceByName: (_result, args: QueryGetWorkspaceByNameArgs, cache) => {
      const fields = cache.inspectFields("Query");
      for (const field of fields) {
        if (
          field.fieldName !== "getWorkspaceByName" &&
          field.fieldName !== "getWorkspaceById"
        ) {
          continue;
        }
        const link = cache.resolve("Query", field.fieldKey);
        if (link == null || typeof link !== "string") continue;
        const name = cache.resolve(link, "name");
        if (name === args.name) {
          return { __typename: "Workspace", id: cache.resolve(link, "id") };
        }
      }
      return undefined;
    },
    getSessionById: (_result, args: QueryGetSessionByIdArgs, cache) => {
      if (!isCached(cache, "Session", args.input.sessionId)) return undefined;
      return { __typename: "Session", id: args.input.sessionId };
    },
    getStyleById: (_result, args: QueryGetStyleByIdArgs, cache) => {
      if (!isCached(cache, "Style", args.input.styleId)) return undefined;
      return { __typename: "Style", id: args.input.styleId };
    },
    getFileById: (_result, args: QueryGetFileByIdArgs, cache) => {
      if (!isCached(cache, "File", args.input.fileId)) return undefined;
      return { __typename: "File", id: args.input.fileId };
    },
    getBlueprintById: (_result, args: QueryGetBlueprintByIdArgs, cache) => {
      if (!isCached(cache, "Blueprint", args.input.blueprintId))
        return undefined;
      return { __typename: "Blueprint", id: args.input.blueprintId };
    },
    getBlueprintVersionById: (
      _result,
      args: QueryGetBlueprintVersionByIdArgs,
      cache,
    ) => {
      if (!isCached(cache, "BlueprintVersion", args.input.blueprintVersionId))
        return undefined;
      return {
        __typename: "BlueprintVersion",
        id: args.input.blueprintVersionId,
      };
    },
    getBlueprintRunById: (
      _result,
      args: QueryGetBlueprintRunByIdArgs,
      cache,
    ) => {
      if (!isCached(cache, "BlueprintRun", args.input.runId)) return undefined;
      return { __typename: "BlueprintRun", id: args.input.runId };
    },
  },
  User: {
    memberships: relayResolver,
    personalAccessTokens: relayResolver,
  },
  Style: {
    postedFiles: relayResolver,
    trainingRuns: relayResolver,
  },
  Workspace: {
    asset: (_result, args: WorkspaceAssetArgs, cache) => {
      if (!args.input.assetId) return undefined;
      if (!isCached(cache, "Asset", args.input.assetId)) return undefined;
      return { __typename: "Asset", id: args.input.assetId };
    },
    style: (_result, args: WorkspaceStyleArgs, cache, info) => {
      const slug =
        args.input.idType === "SLUG" ? args.input.id : args.input.slug;
      if (!slug) return undefined;
      const parentKey = info.parentKey;
      const fields = cache.inspectFields(parentKey);
      for (const field of fields) {
        if (field.fieldName !== "style") continue;
        const link = cache.resolve(parentKey, field.fieldKey);
        if (link == null || typeof link !== "string") continue;
        const cachedSlug = cache.resolve(link, "slug");
        if (cachedSlug === slug) {
          return { __typename: "Style", id: cache.resolve(link, "id") };
        }
      }
      return undefined;
    },
    integrations: relayResolver,
    members: relayResolver,
    styles: relayResolver,
    stylesV2: relayResolver,
    inferences: relayResolver,
    directories: relayResolver,
    files: relayResolver,
    assets: relayResolver,
    library: relayResolver,
    sessions: relayResolver,
    objects: relayResolver,
    blueprints: relayResolver,
  },
  Asset: {
    inferences: relayResolver,
  },
  Session: {
    objects: relayResolver,
    inferences: relayResolver,
    blueprintRuns: relayResolver,
  },
  Blueprint: {
    runs: relayResolver,
  },
  BlueprintVersion: {
    runs: relayResolver,
  },
  BlueprintRun: {
    objects: relayResolver,
  },
  BlueprintRunStep: {
    objects: relayResolver,
  },
};
